// ─── AddressAutocomplete — campo de morada com sugestões geocoded ─────────
//
// Wrapper sobre `geocodeSearchMulti` que mostra um TextField MUI com lista
// de sugestões (Mapbox/HERE) e chama:
//   · onChange(text)    — sempre que o texto muda
//   · onSelect({ name, coordinates: [lng, lat], source }) — ao escolher
//
// Padrão idêntico ao `AddressField` interno do OrderForm (mantemos
// duplicação cirúrgica em vez de extrair de lá para evitar risco no
// formulário gigante de OTs).

import React, { useState, useRef } from 'react';
import { Box, TextField, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { geocodeSearchMulti } from '../../utils/mapboxApi';

const useStyles = makeStyles(() => ({
  wrap: { position: 'relative', width: '100%' },
  suggestions: {
    position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0,
    background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8,
    boxShadow: '0 8px 24px rgba(0,0,0,0.10)', zIndex: 1300,
    maxHeight: 240, overflowY: 'auto',
  },
  item: {
    padding: '8px 12px', cursor: 'pointer', fontSize: '0.78rem',
    borderBottom: '1px solid #f1f5f9',
    '&:hover': { background: '#f8fafc' },
    '&:last-child': { borderBottom: 'none' },
  },
  itemSource: {
    fontSize: '0.6rem', color: '#94a3b8', fontWeight: 600,
    textTransform: 'uppercase', letterSpacing: '0.04em',
  },
}));

function AddressAutocomplete({
  label, value, onChange, onSelect, country = 'PT',
  placeholder, size = 'small', fullWidth = true, disabled = false,
}) {
  const classes = useStyles();
  const [suggestions, setSuggestions] = useState([]);
  const [focused, setFocused] = useState(false);
  const debounceRef = useRef(null);

  function handleChange(e) {
    const val = e.target.value;
    onChange(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      if (val.length >= 3) {
        try {
          const results = await geocodeSearchMulti(val, { country: country || 'PT' });
          setSuggestions(results || []);
        } catch { setSuggestions([]); }
      } else {
        setSuggestions([]);
      }
    }, 350);
  }

  function handleSelect(item) {
    onChange(item.name);
    onSelect(item);
    setSuggestions([]);
    // perde-se foco para não voltar a abrir o popover ao escrever
    setFocused(false);
  }

  return (
    <Box className={classes.wrap}>
      <TextField
        label={label}
        value={value}
        onChange={handleChange}
        onFocus={() => setFocused(true)}
        onBlur={() => setTimeout(() => setFocused(false), 180)}
        variant="outlined"
        size={size}
        fullWidth={fullWidth}
        disabled={disabled}
        placeholder={placeholder}
        autoComplete="off"
      />
      {focused && suggestions.length > 0 && (
        <Box className={classes.suggestions}>
          {suggestions.map((s, i) => (
            <Box
              key={`${s.source}-${i}`}
              className={classes.item}
              onMouseDown={(e) => { e.preventDefault(); handleSelect(s); }}
            >
              <Box style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
                <Typography style={{ fontSize: '0.78rem', color: '#0f172a', flex: 1 }}>
                  {s.name}
                </Typography>
                <Typography className={classes.itemSource}>{s.source}</Typography>
              </Box>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}

export default AddressAutocomplete;
